import { useCallback, useEffect, useState } from 'react';
import type { Cita } from '../types';
import { storage } from '../lib/storage';
import { cancelarNotificacionesDeCita, programarNotificaciones } from '../lib/notifications';
import { esCitaPasada } from '../lib/fechas';
import { encolar } from '../lib/outbox';
import { onDatosCambiados } from '../lib/events';

function extraerCamposParaSync(cita: Cita): Record<string, unknown> {
  // Si la cita esta linkeada a un cliente registrado, no enviamos nombre/telefono:
  // el server resuelve por join al pull. Si es ad-hoc, snapshot directo en la cita.
  return {
    clienteId: cita.clienteId,
    nombre: cita.clienteId ? null : cita.cliente || null,
    telefono: cita.clienteId ? null : cita.telefono || null,
    fecha: cita.fecha,
    hora: cita.hora,
    notas: cita.notas,
    notificacionesSilenciadas: cita.notificacionesSilenciadas,
  };
}

export function useCitas() {
  const [citas, setCitas] = useState<Cita[]>([]);
  const [cargando, setCargando] = useState(true);

  const recargar = useCallback(async () => {
    const lista = await storage.obtenerCitas();
    setCitas(lista);
  }, []);

  useEffect(() => {
    void (async () => {
      await recargar();
      setCargando(false);
    })();
    return onDatosCambiados(() => {
      void recargar();
    });
  }, [recargar]);

  const guardarCita = useCallback(
    async (cita: Cita, esCreate: boolean) => {
      const ahora = new Date().toISOString();
      const conTimestamp: Cita = { ...cita, updatedAt: ahora };
      await storage.guardarCita(conTimestamp);
      await encolar({
        entity: 'cita',
        id: cita.id,
        type: esCreate ? 'create' : 'update',
        data: extraerCamposParaSync(conTimestamp),
        clientUpdatedAt: ahora,
      });
      void programarNotificaciones(conTimestamp);
      await recargar();
    },
    [recargar]
  );

  const eliminarCita = useCallback(
    async (id: string) => {
      try {
        await cancelarNotificacionesDeCita(id);
      } catch (error) {
        console.error('Error al cancelar notificaciones:', error);
      }
      await storage.eliminarCita(id);
      await encolar({
        entity: 'cita',
        id,
        type: 'delete',
        clientUpdatedAt: new Date().toISOString(),
      });
      await recargar();
    },
    [recargar]
  );

  const silenciarCita = useCallback(
    async (cita: Cita) => {
      try {
        await cancelarNotificacionesDeCita(cita.id);
      } catch (error) {
        console.error('Error al silenciar:', error);
        alert('Error al silenciar notificaciones');
        return;
      }
      const ahora = new Date().toISOString();
      const actualizada: Cita = { ...cita, notificacionesSilenciadas: true, updatedAt: ahora };
      await storage.guardarCita(actualizada);
      await encolar({
        entity: 'cita',
        id: cita.id,
        type: 'update',
        data: { notificacionesSilenciadas: true },
        clientUpdatedAt: ahora,
      });
      await recargar();
      alert('🔕 Notificaciones silenciadas correctamente');
    },
    [recargar]
  );

  const citasProximas = citas.filter((c) => !esCitaPasada(c.fecha, c.hora));
  const citasPasadas = citas.filter((c) => esCitaPasada(c.fecha, c.hora));

  return {
    citas,
    citasProximas,
    citasPasadas,
    cargando,
    guardarCita,
    eliminarCita,
    silenciarCita,
  };
}
